import get from 'lodash.get';

import {
    VirtualizedTableColumns,
    VirtualizedTableItem
} from 'services/virtualizedTable/types';
import {
    EquipmentSet,
    CalibrationPressureUnit
} from 'services/equipmentSets/types';
import { selectEquipmentSetsArray } from 'services/equipmentSets/selectors';
import { formatPressureUnit } from 'services/equipmentSets/utils';
import {
    selectSprayerEquipmentOptions,
    selectTractorEquipmentOptions,
    selectNozzleSetEquipmentOptions
} from 'services/equipment/selectors';
import { selectEntityTypeFirstDepth } from 'services/entityTypes/selectors';
import { selectEntityFirstDepthOptions } from 'services/entities/selectors';
import {
    FIELD_TYPE_DROPDOWN,
    FIELD_TYPE_STRING,
    FIELD_TYPE_NUMBER,
    FIELD_TYPE_LIST
} from 'components/VirtualizedTable/constants';
// @ts-ignore
import { selectCurrentSeasonIsComplete } from 'services/currentSeasonId/selectors';
import { makeSelectTableColumnWidths } from 'services/columnWidths/selectors';
import createDeepEqualSelector from 'utils/reselect/typescript/createDeepEqualSelector';
import {
    selectIsAdmin,
    selectIsAssistantManager,
    selectIsManager
} from 'services/usersMe/selectors';

const selectEquipmentSetsTableColumns = createDeepEqualSelector(
    [
        makeSelectTableColumnWidths(),
        selectEntityTypeFirstDepth,
        selectEntityFirstDepthOptions,
        selectTractorEquipmentOptions,
        selectSprayerEquipmentOptions,
        selectNozzleSetEquipmentOptions
    ],
    (
        tableColumnWidths,
        firstDepthEntityType,
        firstDepthEntityOptions,
        tractorEquipmentOptions,
        sprayerEquipmentOptions,
        nozzleSetEquipmentOptions
    ): VirtualizedTableColumns => {
        const result: VirtualizedTableColumns = [
            {
                label: 'Tractor',
                value: 'tractorId',
                width: 250,
                type: FIELD_TYPE_DROPDOWN,
                options: tractorEquipmentOptions,
                validators: 'isRequired',
                disableUpdating: true,
                formColumnWidth: 8
            },
            {
                label: 'Sprayer',
                value: 'sprayerId',
                width: 250,
                type: FIELD_TYPE_DROPDOWN,
                options: sprayerEquipmentOptions,
                validators: 'isRequired',
                disableUpdating: true,
                formColumnWidth: 8
            },
            {
                label: 'Nozzle Set',
                value: 'nozzleSetId',
                width: 250,
                type: FIELD_TYPE_DROPDOWN,
                options: nozzleSetEquipmentOptions,
                validators: 'isRequired',
                disableUpdating: true,
                formColumnWidth: 8
            },
            {
                label: firstDepthEntityType
                    ? firstDepthEntityType.name
                    : 'Area',
                value: 'entityId',
                width: 200,
                type: FIELD_TYPE_DROPDOWN,
                options: firstDepthEntityOptions,
                formColumnWidth: 8
            },
            {
                label: 'Calibrations',
                value: 'calibrations',
                width: 150,
                type: FIELD_TYPE_LIST,
                formClassName: 'full-width-calibrations',
                listColumns: [
                    {
                        label: 'Min Row Width (m)',
                        value: 'rowSpacingMin',
                        type: FIELD_TYPE_NUMBER,
                        validators: [
                            'isRequired',
                            'isPositiveNumber',
                            'maxValue99'
                        ]
                    },
                    {
                        label: 'Max Row Width (m)',
                        value: 'rowSpacingMax',
                        type: FIELD_TYPE_NUMBER,
                        validators: [
                            'isRequired',
                            'isPositiveNumber',
                            'maxValue99'
                        ]
                    },
                    {
                        label: 'Swath Width (m)',
                        value: 'swathWidth',
                        type: FIELD_TYPE_NUMBER,
                        validators: [
                            'isRequired',
                            'isPositiveNumber',
                            'maxValue99'
                        ]
                    },
                    {
                        label: 'Gear',
                        value: 'gear',
                        type: FIELD_TYPE_STRING,
                        validators: 'isRequired'
                    },
                    {
                        label: 'Speed (km/h)',
                        value: 'speed',
                        type: FIELD_TYPE_NUMBER,
                        validators: [
                            'isRequired',
                            'isPositiveNumber',
                            'maxValue99'
                        ]
                    },
                    {
                        label: 'Flow Rate (L/min)',
                        value: 'flowRate',
                        type: FIELD_TYPE_NUMBER,
                        validators: [
                            'isRequired',
                            'isPositiveNumber',
                            'maxValue999'
                        ]
                    },
                    {
                        label: 'Pressure',
                        value: 'gaugePressure',
                        type: FIELD_TYPE_NUMBER,
                        validators: [
                            'isRequired',
                            'isInteger',
                            'isPositiveNumber'
                        ]
                    },
                    {
                        label: 'Press. Unit',
                        value: 'pressureUnit',
                        type: FIELD_TYPE_DROPDOWN,
                        validators: 'isRequired',
                        options: Object.values(CalibrationPressureUnit).map(
                            (unit) => ({
                                key: unit,
                                text: formatPressureUnit(unit),
                                value: unit
                            })
                        )
                    },
                    {
                        label: 'Appl. Rate (L/ha)',
                        value: 'applicationRate',
                        type: FIELD_TYPE_NUMBER,
                        validators: [
                            'isRequired',
                            'isInteger',
                            'isPositiveNumber',
                            'maxValue10000'
                        ]
                    },
                    {
                        label: 'Engine RPM',
                        value: 'engineRpm',
                        type: FIELD_TYPE_NUMBER,
                        validators: [
                            'isRequired',
                            'isInteger',
                            'isPositiveNumber'
                        ]
                    },
                    {
                        label: 'Fan Speed',
                        value: 'fanSpeed',
                        validators: 'isRequired',
                        type: FIELD_TYPE_DROPDOWN,
                        options: [
                            {
                                key: 'High',
                                text: 'High',
                                value: 'High'
                            },
                            {
                                key: 'Low',
                                text: 'Low',
                                value: 'Low'
                            }
                        ]
                    },
                    {
                        label: 'Nozzles/Side',
                        value: 'nozzlesPerSide',
                        type: FIELD_TYPE_NUMBER,
                        validators: ['isPositiveNumber', 'isInteger']
                    }
                ]
            }
        ];

        return result.map((column) => {
            const { width, value } = column;
            return {
                ...column,
                width: get(tableColumnWidths, value, width)
            };
        });
    }
);

const selectEquipmentSetItems = createDeepEqualSelector(
    [selectEquipmentSetsArray],
    (equipmentSetsArray) =>
        equipmentSetsArray.map(
            (equipmentSet: EquipmentSet): VirtualizedTableItem => {
                const { uuid } = equipmentSet;
                return {
                    key: uuid,
                    id: equipmentSet.id,
                    tractorId: equipmentSet.tractorId,
                    sprayerId: equipmentSet.sprayerId,
                    nozzleSetId: equipmentSet.nozzleSetId,
                    entityId: equipmentSet.entities.length
                        ? equipmentSet.entities[0].id
                        : null,
                    calibrations: equipmentSet.calibrations
                };
            }
        )
);

const makeSelectEquipmentSetItems = (): typeof selectEquipmentSetItems =>
    selectEquipmentSetItems;

const selectWriteAccess = createDeepEqualSelector(
    selectIsAdmin,
    selectIsManager,
    selectIsAssistantManager,
    selectCurrentSeasonIsComplete,
    (
        isAdmin,
        isManager,
        isAssistantManager,
        currentSeasonIsComplete
    ): boolean =>
        Boolean(
            (isAdmin || isManager || isAssistantManager) &&
                !currentSeasonIsComplete
        )
);

export {
    selectEquipmentSetsTableColumns,
    makeSelectEquipmentSetItems,
    selectWriteAccess
};
